import { useMemo } from 'react'

import { logAndCaptureMessage } from '../../../utils/errorTools'
import { isStateMachineClaim, userClaimIds } from '../../../utils/userClaims'
import { useAuth } from '../../auth'
import { OnboardPatientContext, OnboardPatientStateValue } from '../state/machine-patient'
import { isValidPatientState } from '../utils'

export interface UseOnboardPatientClaim {
  claim: OnboardPatientClaimParsed | undefined
  isOnboardingComplete: boolean
}

export interface OnboardPatientClaimParsed {
  state: OnboardPatientStateValue
  context: OnboardPatientContext
}

export const useOnboardPatientClaim = (): UseOnboardPatientClaim => {
  const { user } = useAuth()

  const claim = useMemo<OnboardPatientClaimParsed | undefined>(() => {
    const claimValue = user ? (user[userClaimIds.onboardPatient] as unknown) : undefined
    if (!isStateMachineClaim(claimValue) || !claimValue.state) {
      return undefined
    }
    const state = claimValue.state.trim()
    const context = claimValue.context ?? {}
    if (!isValidPatientState(state)) {
      logAndCaptureMessage(`Invalid onboard patient state "${state}" in user claim`, {
        level: 'warning',
      })
      return undefined
    }
    return {
      state,
      context,
    }
  }, [user])

  const isOnboardingComplete = useMemo(() => !claim || claim.state === 'complete', [claim])

  return { claim, isOnboardingComplete }
}
